/**
 * FIX-3b (prod fixes 2026-10-02, UX-SPEC.md section 3.3 / 3.5, task T5): how a single-file COMPRESS card presents its
 * size outcome -- the size line, the "Original kept" chip, and where "Make it smaller" goes in the action row.
 *
 * The server decides the outcome (T4, `ProcessResult.sizeOutcome`: `shrank | no_reduction | grew | original_kept`, set
 * for single-file compress only); this module decides presentation only, from that code. It never reads the human note
 * text (the notes themselves come from the card's notes renderer, `processingNotes` / `noteCodes`).
 *
 * A card without `sizeOutcome` (every card from before the server change, an edge on an older jar, every convert page)
 * renders exactly as before except that the percentage is an integer ("50%", not "50.00%").
 *
 * Pure and dependency-free so the formatting matrix is unit-tested without the card.
 */

/** UX-SPEC section 13 copy, verbatim (X3-S1, X3-S2, X3-C1, X3-B1, X3-B2). `X3-S2-same` is section 3.3's ">= 99.5%" wording. */
export const COMPRESS_OUTCOME_COPY = {
    'X3-S1': 'Size: {size} (your original, unchanged)',
    'X3-S2': 'New size: {size} ({pct}% of the original {orig})',
    'X3-S2-same': 'New size: {size} (about the same as the original {orig})',
    'X3-C1': 'Original kept',
    'X3-B1': 'Make it smaller',
    'X3-B2': 'We\'ve picked a stronger setting: {label}. You can change it.',
} as const;

/** `{name}` placeholders; an unknown placeholder is left as written so a missing param is visible, never "undefined". */
export function fmtOutcomeCopy(template: string, params: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (whole, name) => (name in params ? String(params[name]) : whole));
}

export type SizeOutcome = 'shrank' | 'no_reduction' | 'grew' | 'original_kept';
const SIZE_OUTCOMES: ReadonlySet<string> = new Set(['shrank', 'no_reduction', 'grew', 'original_kept']);

/** Where "Make it smaller" sits for an outcome: the primary on a kept original, a secondary key on no reduction, else none. */
export type MakeSmallerPlacement = 'primary' | 'secondary' | null;

/** The note codes (T1 contract C1) that say the delivered file is not smaller -- a warning about the same thing the chip says. */
const SIZE_WARNING_CODES: ReadonlySet<string> = new Set(['COMPRESS_NO_REDUCTION', 'COMPRESS_GREW']);

interface NoteCodeLike { code?: unknown; kind?: unknown }

/** The structural slice of a card this module reads; `FileUpload` satisfies it. */
export interface CompressOutcomeCard {
    processType?: unknown;
    size?: unknown;
    result?: {
        sizeOutcome?: unknown;
        noteCodes?: unknown;
        sizes?: { outputs?: { [path: string]: unknown } };
    } | null;
}

/** `ProcessTypes.COMPRESS` -- the literal, so this module needs nothing from the types barrel. */
const COMPRESS = 'compress';

/** The server's verdict for a single-file compress card, or `null` (old card, convert page, unknown value). */
export function sizeOutcomeOf(card: CompressOutcomeCard | null | undefined): SizeOutcome | null {
    if (!card || card.processType !== COMPRESS) return null;
    const raw = card.result?.sizeOutcome;
    return typeof raw === 'string' && SIZE_OUTCOMES.has(raw) ? raw as SizeOutcome : null;
}

/** Does the card carry a size warning note (COMPRESS_NO_REDUCTION / COMPRESS_GREW) in its structured codes? */
export function hasSizeWarningNote(card: CompressOutcomeCard | null | undefined): boolean {
    const codes = card?.result?.noteCodes;
    if (!Array.isArray(codes)) return false;
    return codes.some((c: NoteCodeLike) => typeof c?.code === 'string' && SIZE_WARNING_CODES.has(c.code));
}

/**
 * The neutral "Original kept" chip: only on a kept original, and never together with a size warning about the same file
 * (UX-SPEC 3.4 "Don't show both a chip and a warning for the same thing"). The server never sends both; if it ever did,
 * the warning (the more specific statement) is what the user sees.
 */
export function showsOriginalKeptChip(card: CompressOutcomeCard | null | undefined): boolean {
    return sizeOutcomeOf(card) === 'original_kept' && !hasSizeWarningNote(card);
}

export function makeSmallerPlacementOf(outcome: SizeOutcome | null): MakeSmallerPlacement {
    if (outcome === 'original_kept') return 'primary';
    if (outcome === 'no_reduction') return 'secondary';
    return null;
}

/**
 * The card's "Compressed WAV" process line (UX-SPEC 3.4, UX round 2 UX2-06): not over a kept original, and not over a
 * no_reduction size line ("about the same as the original" / "97% of the original") -- the size line and its note say
 * what happened. Shrank, grew (as today, 3.3) and cards without `sizeOutcome` keep it.
 */
export function showsCompressedHeading(card: CompressOutcomeCard | null | undefined): boolean {
    const outcome = sizeOutcomeOf(card);
    return outcome !== 'original_kept' && outcome !== 'no_reduction';
}

/** Output bytes of a single-output card (`result.sizes.outputs`' one entry), or NaN. */
export function singleOutputBytes(card: CompressOutcomeCard | null | undefined): number {
    const outputs = card?.result?.sizes?.outputs;
    if (!outputs || typeof outputs !== 'object') return NaN;
    const keys = Object.keys(outputs);
    return keys.length > 0 ? parseFloat(String(outputs[keys[0]])) : NaN;
}

/**
 * The integer percentage of the original the output is: 50, 97, 111. Below 1 % (a real but tiny file) reads "<1"
 * rather than "0", which would say the file is empty. NaN in, `null` out.
 */
export function percentOfOriginal(outputBytes: number, inputBytes: number): string | null {
    if (!Number.isFinite(outputBytes) || !Number.isFinite(inputBytes) || inputBytes <= 0 || outputBytes < 0) return null;
    const pct = (100 * outputBytes) / inputBytes;
    if (outputBytes > 0 && pct < 0.5) return '<1';
    return String(Math.round(pct));
}

/** UX-SPEC 3.3: at >= 99.5 % (and below what would round to 101 %) the line says "about the same as the original". */
export function isAboutTheSame(outputBytes: number, inputBytes: number): boolean {
    if (!Number.isFinite(outputBytes) || !Number.isFinite(inputBytes) || inputBytes <= 0) return false;
    const pct = (100 * outputBytes) / inputBytes;
    return pct >= 99.5 && pct < 100.5;
}

/**
 * The size line under a single-file compress card.
 * - `original_kept`: X3-S1 "Size: 4.6 MB (your original, unchanged)" -- never "New size", the file is not new.
 * - any other server outcome: X3-S2 with an integer percent; "about the same as the original" at >= 99.5 %.
 * - no outcome (old card / older edge): X3-S2 with an integer percent, nothing else changes.
 * - no readable original size: "New size: {size}" alone. `null` only when the output size cannot be read either.
 */
export function compressSizeLine(card: CompressOutcomeCard, fmtBytes: (bytes: number) => string): string | null {
    const outcome = sizeOutcomeOf(card);
    const out = singleOutputBytes(card);
    const input = typeof card?.size === 'number' ? card.size : parseFloat(String(card?.size));
    if (outcome === 'original_kept') {
        const kept = Number.isFinite(out) ? out : input;
        return Number.isFinite(kept) ? fmtOutcomeCopy(COMPRESS_OUTCOME_COPY['X3-S1'], { size: fmtBytes(kept) }) : null;
    }
    const pct = percentOfOriginal(out, input);
    if (pct === null) {
        // No readable original size (an old card without `size`): the new size alone, as the line always led with it --
        // never "NaN%", and never an empty line where the card used to say how big the result is.
        return Number.isFinite(out) && out >= 0 ? `New size: ${fmtBytes(out)}` : null;
    }
    const params = { size: fmtBytes(out), orig: fmtBytes(input), pct };
    if (outcome !== null && isAboutTheSame(out, input)) return fmtOutcomeCopy(COMPRESS_OUTCOME_COPY['X3-S2-same'], params);
    return fmtOutcomeCopy(COMPRESS_OUTCOME_COPY['X3-S2'], params);
}

/**
 * `compress.size_outcome` is sent once per card, on the first terminal render this browser sees -- not again on every
 * visit to /downloads. The ids already reported live in one bounded localStorage list; a store that cannot be read or
 * written falls back to this page's memory (the event may then repeat on a later visit, never within one).
 */
export const SIZE_OUTCOME_REPORTED_KEY = 'XC_SIZE_OUTCOME_REPORTED';
export const SIZE_OUTCOME_REPORTED_MAX = 300;
const reportedInMemory = new Set<string>();

export interface KeyValueStore { getItem(key: string): string | null; setItem(key: string, value: string): void }

/** True exactly once per id (first call); every later call for the same id is false. Never throws. */
export function claimSizeOutcomeReport(id: string, store: KeyValueStore | null | undefined): boolean {
    if (!id) return false;
    if (reportedInMemory.has(id)) return false;
    reportedInMemory.add(id);
    if (!store) return true;
    try {
        const raw = store.getItem(SIZE_OUTCOME_REPORTED_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        const ids: string[] = Array.isArray(parsed) ? parsed.filter((x: unknown) => typeof x === 'string') : [];
        if (ids.includes(id)) return false;
        ids.push(id);
        store.setItem(SIZE_OUTCOME_REPORTED_KEY, JSON.stringify(ids.slice(-SIZE_OUTCOME_REPORTED_MAX)));
    } catch { /* memory guard above still holds for this page */ }
    return true;
}

/** Test seam: forget this page's in-memory guard. */
export function resetSizeOutcomeReportsForTest(): void {
    reportedInMemory.clear();
}
